<template>
  <div
    v-if="isLatestFeatureUnlocked"
    id="latest-dashboard"
    ref="latest-dashboard"
  >
    <section class="grid pb-5">
      <v-container class="left pa-0">
        <v-container class="filter pa-0">
          <LatestArea>
            <LatestFilters
              @change-params="handleChangeParams"
              @updated="handleDOMUpdate"
              ref="latest-filters"
            ></LatestFilters>
          </LatestArea>
        </v-container>
        <v-container class="left-bot pa-0">
          <LatestArea
            :slots="eventsSlots"
            :show-picker="true"
            :preselected-slot="eventsSlotsSelected"
            @slot-change="handleEventsSlotChange"
          >
            <template #hotspots>
              <LatestHotspots
                :params="params"
                :draggable="enableDrag"
                :analytics-category="hotspotsAnalyticsCategory"
                :force-update="lastUpdate"
                @open-hotspot-details="$emit(EMITTED_ACTIONS.CLOSE, $event)"
              ></LatestHotspots>
            </template>
            <template #headlines>
              <Headlines
                :params="params"
                :draggable="enableDrag"
                :analytics-category="headlinesAnalyticsCategory"
                @updated="handleDOMUpdate"
              ></Headlines>
            </template>
          </LatestArea>
        </v-container>
      </v-container>

      <v-container class="mid pa-0">
        <LatestArea title="Changes in Pulse">
          <PulseChanges
            :params="params"
            :openedTimestamp="openedTimestamp"
            :draggable="enableDrag"
            :analytics-category="pulseChangesAnalyticsCategory"
            :analytics-category-numdays="pulseChangesNumDaysAnalyticsCategory"
            :analytics-category-sort="pulseChangesSortAnalyticsCategory"
            :analytics-category-filter="pulseChangesFilterAnalyticsCategory"
            :analytics-category-country-link="
              pulseChangesCountryLinkAnalyticsCategory
            "
          ></PulseChanges>
        </LatestArea>
      </v-container>

      <v-container class="right pa-0">
        <v-container class="right-top pa-0">
          <LatestArea
            title="Elections Watch"
            digest="elections-monitor"
            tooltipOn="SUBSCRIBE to receive a monthly digest analysing elections scheduled in the next two months, delivered on the first of the month."
            tooltipOff="UNSUBSCRIBE"
            :analytics-category="electionsAnalyticsCategory"
          >
            <FutureEventsTimelineHolder
              :openedTimestamp="openedTimestamp"
              :params="params"
            />
          </LatestArea>
        </v-container>
        <v-container class="right-bot pa-0 mt-1">
          <LatestArea
            title="Evacuation and Risk Rating Changes"
            :digest="digestEvacRiskRating"
            tooltipOn="SUBSCRIBE to be alerted when we make changes to risk ratings or evacuation levels for any country."
            tooltipOff="UNSUBSCRIBE"
            :analytics-category="riskRatingAnalyticsCategory"
          >
            <RiskRatingChanges
              :draggable="enableDrag"
              :params="params"
              :analytics-category="riskRatingAnalyticsCategory"
            ></RiskRatingChanges>
          </LatestArea>
        </v-container>
      </v-container>
    </section>
  </div>
</template>

<script>
import { SAVE_PREFERENCES } from "@/store/actions.type";

const EMITTED_ACTIONS = {
  CLOSE: "close",
};

import { LATEST_CATEGORIES, EVENT_ACTIONS } from "@/common/constants/analytics";
import { MODULE_PREFIX_USER } from "@/store";
import { mapGetters } from "vuex";

export default {
  name: "Latest",
  components: {
    LatestHotspots: () => import("@/components/latest/LatestHotspots"),
    LatestFilters: () => import("@/components/latest/LatestFilters"),
    FutureEventsTimelineHolder: () =>
      import("@/components/futureEvents/FutureEventsTimelineHolder"),
    Headlines: () => import("@/components/latest/Headlines"),
    LatestArea: () => import("@/components/latest/LatestArea"),
    RiskRatingChanges: () =>
      import("@/components/controlRisks/risks/RiskRatingChanges"),
    PulseChanges: () => import("@/components/grids/PulseChanges"),
  },
  props: {
    openedTimestamp: {
      type: String,
    },
  },
  data: () => ({
    enableDrag: false,
    params: {},
    latestPreferences: {},
    headlinesAnalyticsCategory: LATEST_CATEGORIES.headlines,
    pulseChangesAnalyticsCategory: LATEST_CATEGORIES.pulse_changes,
    pulseChangesNumDaysAnalyticsCategory:
      LATEST_CATEGORIES.pulse_changes_num_days,
    pulseChangesSortAnalyticsCategory: LATEST_CATEGORIES.pulse_changes_sort,
    pulseChangesFilterAnalyticsCategory: LATEST_CATEGORIES.pulse_changes_filter,
    pulseChangesCountryLinkAnalyticsCategory:
      LATEST_CATEGORIES.pulse_changes_item,
    riskRatingAnalyticsCategory: LATEST_CATEGORIES.risk_rating_changes,
    electionsAnalyticsCategory: LATEST_CATEGORIES.elections,
    hotspotsAnalyticsCategory: LATEST_CATEGORIES.hotspots,
    eventsSlotsSelected: "hotspots",
    lastUpdate: new Date().valueOf(),
  }),
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    isLatestFeatureUnlocked() {
      return this.featureUnlocked("latest-dashboard");
    },
    digestEvacRiskRating() {
      if (!this.featureUnlocked("notifications")) {
        return "evacuation-watch,risk-rating";
      }
      return "";
    },
    /**
     * A list of slot configurations for the events (Headlines) section
     * @returns {[{text: string, value: string, analyticsCategory: string},{text: string, value: string, analyticsCategory: string}]}
     */
    eventsSlots() {
      return [
        {
          text: "Hotspots",
          value: "hotspots",
          analyticsCategory: LATEST_CATEGORIES.hotspots,
        },
        {
          text: "Headlines",
          value: "headlines",
          analyticsCategory: LATEST_CATEGORIES.headlines,
        },
      ];
    },
    /**
     * The preselected slot for the events area, grabbed from user preferences.
     * @returns {string}
     * @default hotspots
     */
    eventsSlotsSelectedPref() {
      return this.latestPreferences?.headlinesEnabled
        ? "headlines"
        : "hotspots";
    },
  },
  created() {
    this.EMITTED_ACTIONS = EMITTED_ACTIONS;

    this.applyPrefs();
  },
  methods: {
    applyPrefs() {
      this.latestPreferences = this.preferences?.latest || {};

      //Update the selected slot from user prefs
      this.eventsSlotsSelected = this.eventsSlotsSelectedPref;
    },
    setPrefs() {
      //Make sure default values are set
      if (!this.latestPreferences) {
        this.latestPreferences = {};
      }
      if (!this.preferences) {
        this.preferences = {};
      }

      //Update the hotspots/headlines toggle
      if (this.eventsSlotsSelected === "hotspots") {
        this.latestPreferences.hotspotsEnabled = true;
        this.latestPreferences.headlinesEnabled = false;
      } else if (this.eventsSlotsSelected === "headlines") {
        this.latestPreferences.headlinesEnabled = true;
        this.latestPreferences.hotspotsEnabled = false;
      }

      //Set the latest preferences on the parent preferences object
      this.preferences.latest = this.latestPreferences;

      //Dispatch the changes
      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
    handleChangeParams(params) {
      this.params = params;
    },
    /**
     * Sets the given slot as selected and saves to user profile
     * @param {string} selectedSlot}
     */
    handleEventsSlotChange(selectedSlot) {
      this.eventsSlotsSelected = selectedSlot;

      this.handlePageTrackerEvent(
        LATEST_CATEGORIES[selectedSlot],
        EVENT_ACTIONS.selected,
        ""
      );

      this.setPrefs();
    },
    handleDOMUpdate() {
      //Track when the DOM was last updated. Child components listen to this to react to DOM changes
      // (particularly for setting height)
      this.lastUpdate = new Date().valueOf();
    },
  },
};
</script>

<style scoped>
#latest-dashboard {
  width: calc(100vw - 245px);
  height: calc(100vh - 60px);
}

.grid {
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
  display: grid;
  grid-template-columns: 31.666% 31.666% 31.666%;
  grid-template-areas: "left mid right";
  column-gap: 20px;
  row-gap: 20px;
}

.filter {
  min-height: 150px;
}
.left {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.left-bot {
  grid-area: left-bot;
}

.mid {
  grid-area: mid;
}

.right {
  grid-area: right;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows:
    calc((100vh - 60px) / 2 - 20px)
    calc((100vh - 60px) / 2 - 20px);
  grid-template-areas:
    "right-top"
    "right-bot";
  grid-row-gap: 20px;
}

.right-top {
  grid-area: right-top;
  overflow: hidden;
}

.right-bot {
  grid-area: right-bot;
  overflow: hidden;
}
</style>
